/**
 * Vorstands-Dashboard
 *
 * Struktur und Inhalt folgen dem Claude-Design-Entwurf `Vorstands-Dashboard.dc.html`
 * (Projekt d33fb6d7-e56c-4f55-8efd-dd8640e1d0c2): Begrüßung + Layout-/Zeitraum-Tabs,
 * vier KPIs, sechs Karten (Termine, Gäste, Finanzen, Hunde, Mitglieder,
 * Handlungsbedarf) in drei umschaltbaren Rastern. Dazu — wie vor dem Neubau —
 * eine Karte „Geburtstage" (Daten aus GET /api/members/birthdays).
 *
 * Gestaltung folgt dem Projekt-Designsystem, nicht dem CSS des Entwurfs:
 *  - Tailwind + DESIGN_SPEC.md (keine Gradienten, Cards mit Border, dark:bg-[#1c2432])
 *  - KPI-Zeile über StatGroup/StatCard  → Issue #10 (mobiles Statistik-Muster)
 *  - Icons ausschließlich aus attributeIconMap → Issue #9 (Heroicons-Outline-Matrix)
 *  - Karten über WidgetShell
 *
 * Daten kommen aus GET /api/dashboard/vorstand (siehe useVorstandsDashboard);
 * nur die Geburtstage nutzen die bestehende Mitglieder-Route (useUpcomingBirthdays).
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { hasAdminRights } from '../utils/roleUtils';
import { ArrowRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { getAttributeIcon, type AttributeIconKey } from '../utils/attributeIcons';
import { StatCard, StatGroup } from './StatCard';
import WidgetShell from './widgets/WidgetShell';
import MemberAvatar from './MemberAvatar';
import DogDetailModal from './modals/DogDetailModal';
import GuestDetailModal from './modals/GuestDetailModal';
import GuestBadge from './GuestBadge';
import EventModal from './EventModal';
import EditEventModal from './EditEventModal';
import { fetchCalendarEvent, type CalendarEvent } from '../utils/calendarEvents';
import useVorstandsDashboard, {
  type AccountIconKey,
  type DocStatus,
  type Period,
  type RoleKey,
} from '../hooks/useVorstandsDashboard';
import useUpcomingBirthdays, { type UpcomingBirthday } from '../hooks/useUpcomingBirthdays';

type Layout = 'reihen' | 'spalten' | 'kacheln';

/* ------------------------------------------------------------------ *
 * Tabs
 * ------------------------------------------------------------------ */

const LAYOUT_TABS: { key: Layout; label: string; title: string }[] = [
  { key: 'reihen', label: 'Reihen', title: 'Ausgeglichene Reihen — Karten einer Zeile sind gleich hoch' },
  { key: 'spalten', label: 'Spalten', title: 'Zwei Spalten im Fluss — keine Höhenabgleiche nötig' },
  { key: 'kacheln', label: 'Kacheln', title: 'Gleichmäßiges 3-Spalten-Raster' },
];

const PERIOD_TABS: { key: Period; label: string }[] = [
  { key: 'monat', label: 'Monat' },
  { key: 'quartal', label: 'Quartal' },
  { key: 'jahr', label: 'Jahr' },
];

/** Zeitraum als Fließtext („… in diesem Monat"). */
const PERIOD_PHRASE: Record<Period, string> = {
  monat: 'in diesem Monat',
  quartal: 'in diesem Quartal',
  jahr: 'in diesem Jahr',
};

/** Raster-Bezeichnung der Balkendiagramme je Zeitraum. */
const PERIOD_BUCKETS: Record<Period, string> = {
  monat: 'je Kalenderwoche',
  quartal: 'je Monat',
  jahr: 'je Monat',
};

/* ------------------------------------------------------------------ *
 * Raster der drei Layout-Varianten (statische Tailwind-Klassen)
 * ------------------------------------------------------------------ */

type CellKey = 'termine' | 'gaeste' | 'finanzen' | 'hunde' | 'mitglieder' | 'todos' | 'geburtstage';

const GRID: Record<Layout, string> = {
  reihen: 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6 xl:gap-5',
  spalten: 'columns-1 gap-4 md:columns-2 xl:gap-5',
  kacheln: 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 xl:gap-5',
};

const CELL: Record<Layout, Record<CellKey, string>> = {
  reihen: {
    termine: 'md:col-span-2 xl:col-span-4',
    gaeste: 'md:col-span-1 xl:col-span-2',
    finanzen: 'md:col-span-1 xl:col-span-2',
    hunde: 'md:col-span-2 xl:col-span-4',
    mitglieder: 'md:col-span-2 xl:col-span-4',
    todos: 'md:col-span-2 xl:col-span-2',
    // Volle Breite: die Geburtstage liegen als Kacheln nebeneinander.
    geburtstage: 'md:col-span-2 xl:col-span-6',
  },
  spalten: {
    termine: 'mb-4 break-inside-avoid xl:mb-5',
    gaeste: 'mb-4 break-inside-avoid xl:mb-5',
    finanzen: 'mb-4 break-inside-avoid xl:mb-5',
    hunde: 'mb-4 break-inside-avoid xl:mb-5',
    mitglieder: 'mb-4 break-inside-avoid xl:mb-5',
    todos: 'mb-4 break-inside-avoid xl:mb-5',
    geburtstage: 'mb-4 break-inside-avoid xl:mb-5',
  },
  kacheln: {
    termine: 'md:col-span-2',
    gaeste: 'md:col-span-1',
    finanzen: 'md:col-span-1',
    hunde: 'md:col-span-2',
    mitglieder: 'md:col-span-2',
    todos: 'md:col-span-1',
    geburtstage: 'md:col-span-2 xl:col-span-3',
  },
};

/* ------------------------------------------------------------------ *
 * Icon-Zuordnung (ausschließlich über die zentrale Matrix)
 * ------------------------------------------------------------------ */

const ACCOUNT_ICON: Record<AccountIconKey, AttributeIconKey> = {
  bank: 'bankAccount',
  cash: 'cashBox',
  drinks: 'drinksAccount',
  guestCards: 'booking',
  savings: 'reserves',
};

const ROLE_LABEL: Record<RoleKey, string> = {
  vorstand: 'Vorstand',
  trainer: 'Trainer',
  finanzpruefer: 'Finanzprüfer',
  mitglied: 'Mitglied',
  gast: 'Gast',
};

/** Balkenfarbe der Rollenverteilung — semantisch, keine Dekoration. */
const ROLE_BAR: Record<RoleKey, string> = {
  mitglied: 'bg-gray-400 dark:bg-gray-500',
  trainer: 'bg-info-500',
  gast: 'bg-gray-300 dark:bg-gray-600',
  vorstand: 'bg-primary-600',
  finanzpruefer: 'bg-warning-500',
};

const ROLE_ICON: Record<RoleKey, AttributeIconKey> = {
  vorstand: 'role',
  trainer: 'trainer',
  finanzpruefer: 'audit',
  mitglied: 'member',
  gast: 'guest',
};

/** Status → Badge-Darstellung. Text trägt die Information, Farbe unterstützt nur. */
const DOC_BADGE: Record<DocStatus, { cls: string; icon: AttributeIconKey }> = {
  valid: {
    cls: 'bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-300',
    icon: 'active',
  },
  soon: {
    cls: 'bg-warning-50 text-warning-700 dark:bg-warning-900/20 dark:text-warning-300',
    icon: 'pending',
  },
  overdue: {
    cls: 'bg-error-50 text-error-700 dark:bg-error-900/20 dark:text-error-300',
    icon: 'error',
  },
  pending: {
    cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300',
    icon: 'pending',
  },
  missing: {
    cls: 'bg-error-50 text-error-700 dark:bg-error-900/20 dark:text-error-300',
    icon: 'inactive',
  },
};

/* ------------------------------------------------------------------ *
 * Formatierung
 * ------------------------------------------------------------------ */

const eur = (n: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);

const eur0 = (n: number) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);

const dec1 = (n: number) =>
  n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const WD = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** Tageszeit-abhängige Anrede (gleiche Schwellen wie im übrigen Frontend). */
const greeting = (now = new Date()) => {
  const h = now.getHours();
  if (h < 12) return 'Guten Morgen';
  if (h < 18) return 'Guten Tag';
  return 'Guten Abend';
};

const parseDay = (v?: string | null) => (v ? new Date(`${v.slice(0, 10)}T00:00:00`) : null);

const ddmm = (v?: string | null) => {
  const d = parseDay(v);
  return d ? `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.` : '—';
};

/* ------------------------------------------------------------------ *
 * Kleine Bausteine
 * ------------------------------------------------------------------ */

/** Icon aus der zentralen Matrix; rendert nichts bei unbekanntem Schlüssel. */
const MatrixIcon: React.FC<{ iconKey: AttributeIconKey; className?: string }> = ({
  iconKey,
  className = 'h-4 w-4',
}) => {
  const Icon = getAttributeIcon(iconKey);
  return Icon ? <Icon className={className} aria-hidden="true" focusable="false" /> : null;
};

const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <p
    className={`text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 ${className}`.trim()}
  >
    {children}
  </p>
);

const Skeleton: React.FC<{ rows?: number; height?: string }> = ({ rows = 4, height = 'h-9' }) => (
  <div className="space-y-2.5">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className={`${height} animate-pulse rounded-lg bg-gray-100 dark:bg-gray-700/50`} />
    ))}
  </div>
);

/**
 * Balkenspalte für die Diagramme.
 *
 * Wichtig: nur die Balkenfläche hat eine feste Höhe (`trackClass`) — Wert und
 * Label stehen außerhalb, damit ein 100-%-Balken sie nicht aus der Box drückt.
 * Nullwerte erhalten einen dünnen Grundstrich, damit die Spalte sichtbar bleibt.
 */
const BarColumn: React.FC<{
  label: string;
  value?: React.ReactNode;
  trackClass?: string;
  title?: string;
  children: React.ReactNode;
}> = ({ label, value, trackClass = 'h-20', title, children }) => (
  <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5" title={title}>
    {value != null && (
      <span className="text-[10px] tabular-nums text-gray-500 dark:text-gray-400">{value}</span>
    )}
    <div className={`flex w-full items-end justify-center gap-0.5 ${trackClass}`}>{children}</div>
    <span className="w-full truncate text-center text-[10px] leading-tight text-gray-400 dark:text-gray-500">
      {label}
    </span>
  </div>
);

/** Balkenhöhe in Prozent; 0 wird zu einem sichtbaren Grundstrich. */
const barHeight = (value: number, max: number) =>
  value <= 0 ? '2px' : `${Math.max(4, Math.round((value / max) * 100))}%`;

/**
 * Zusatztext einer KPI-Kachel in zwei Längen.
 *
 * Auf dem Smartphone stehen pro Kachel nur ~170 px zur Verfügung; die volle
 * Fassung bricht dort unschön um (Issue #10: „Zusatztext steht kompakt unter der
 * Kennzahl"). Ab `sm` wird die vollständige Angabe gezeigt.
 */
const StatSub: React.FC<{ short: string; full: string }> = ({ short, full }) => (
  <>
    <span className="sm:hidden">{short}</span>
    <span className="hidden sm:inline">{full}</span>
  </>
);

const EmptyHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="py-1 text-sm text-gray-500 dark:text-gray-400">{children}</p>
);

/** Statusbadge mit Icon + Text (Text allein trägt die Information). */
const DocBadge: React.FC<{ status: DocStatus; children: React.ReactNode }> = ({
  status,
  children,
}) => {
  const b = DOC_BADGE[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${b.cls}`}
    >
      <MatrixIcon iconKey={b.icon} className="h-3 w-3" />
      {children}
    </span>
  );
};

/** Sekundäraktion im Kartenkopf („Kalender →"). */
const HeaderLink: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
  >
    {label}
    <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
  </button>
);

/** Restzeit bis zum Geburtstag als Text — der Text trägt die Information. */
const birthdayCountdown = (days: number) => {
  if (days === 0) return 'Heute';
  if (days === 1) return 'Morgen';
  if (days < 7) return `In ${days} Tagen`;
  const weeks = Math.round(days / 7);
  return weeks === 1 ? 'In 1 Woche' : `In ${weeks} Wochen`;
};

/**
 * Kachel eines anstehenden Geburtstags. Eigener Bildfehler-Status, damit ein
 * fehlendes Foto nur diese Kachel auf die Initialen zurückfallen lässt.
 */
const BirthdayTile: React.FC<{ b: UpcomingBirthday; baseUrl: string }> = ({ b, baseUrl }) => {
  const [imageError, setImageError] = useState(false);
  const isToday = b.daysUntil === 0;
  const token = localStorage.getItem('jwt_token');
  const photoUrl =
    b.photoVersion && token
      ? `${baseUrl}/api/members/user-image-url/${b.id}?size=S&token=${token}&t=${b.photoVersion}`
      : undefined;
  const dateLabel = b.date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });

  return (
    <li
      className={`flex items-center gap-3 rounded-xl border p-3 ${
        isToday
          ? 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20'
          : 'border-gray-200 dark:border-gray-700'
      }`}
      title={`${b.vorname} ${b.name} — ${b.date.toLocaleDateString('de-DE')}`}
    >
      <MemberAvatar
        member={{ vorname: b.vorname, name: b.name }}
        role="member"
        photoUrl={photoUrl}
        hasImageError={imageError}
        onImageError={() => setImageError(true)}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
          {`${b.vorname} ${b.name}`.trim()}
        </div>
        <div className="flex flex-wrap items-center gap-x-1.5 text-[11px] text-gray-500 dark:text-gray-400">
          <span>wird {b.age}</span>
          {b.isJubilee && (
            <span className="font-semibold text-warning-600 dark:text-warning-400">· Jubiläum</span>
          )}
        </div>
      </div>
      <div className="flex-none text-right">
        <div
          className={`text-xs font-semibold ${
            isToday ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-200'
          }`}
        >
          {birthdayCountdown(b.daysUntil)}
        </div>
        <div className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400">{dateLabel}</div>
      </div>
    </li>
  );
};

/**
 * Ist der Viewport mindestens `sm` (640 px) breit?
 *
 * Unterhalb davon liegen die Karten immer in einer Spalte — das Layout wird dort
 * fest auf „Spalten" gesetzt und die Auswahl ausgeblendet.
 */
function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isDesktop;
}

/**
 * Tab-Gruppe im Projekt-Muster (wie /my-bookings): grauer Träger mit `rounded-xl`,
 * Buttons `h-11` und `font-bold`, aktiver Tab weiß mit Schatten. Mobil füllen die
 * Tabs die Breite, ab `sm` nur so breit wie nötig.
 */
function TabGroup<T extends string>({
  tabs,
  active,
  onChange,
  ariaLabel,
}: {
  tabs: { key: T; label: string; title?: string }[];
  active: T;
  onChange: (k: T) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="flex w-full gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-700 sm:w-fit"
    >
      {tabs.map((t) => {
        const on = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            title={t.title}
            onClick={() => onChange(t.key)}
            className={`flex h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 text-sm font-bold transition-all sm:flex-none sm:px-5 ${
              on
                ? 'bg-white text-gray-900 shadow-sm dark:bg-[#1c2432] dark:text-white'
                : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Dashboard
 * ------------------------------------------------------------------ */

const Dashboard: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const navigate = useNavigate();
  const hasAccess = hasAdminRights(user);

  const [layout, setLayout] = useState<Layout>('reihen');
  const isDesktop = useIsDesktop();
  // Mobil immer der Spalten-Fluss, unabhängig von der Auswahl.
  const effectiveLayout: Layout = isDesktop ? layout : 'spalten';
  const [period, setPeriod] = useState<Period>('monat');
  // Hundedetails: Klick auf eine Zeile der Hundeübersicht öffnet das Detail-Modal.
  const [selectedDog, setSelectedDog] = useState<{
    id: number;
    name: string;
    owner: string;
    ownerIsGuest: boolean;
  } | null>(null);
  // Termin-Details: dasselbe EventModal wie im Kalender.
  const [eventModal, setEventModal] = useState<{
    event: CalendarEvent;
    sameDay: CalendarEvent[];
  } | null>(null);
  const [eventLoadingId, setEventLoadingId] = useState<number | null>(null);
  // Gastdetails: dasselbe Modal wie in der Gästekarten-Verwaltung.
  const [selectedGuest, setSelectedGuest] = useState<{
    mg_nr: string;
    vorname: string;
    name: string;
    mitglied_nr?: string;
  } | null>(null);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);

  const { data, loading, error, refresh } = useVorstandsDashboard(hasAccess, period);
  const { kpi, open, dogStats } = data;
  const birthdays = useUpcomingBirthdays(hasAccess);
  const birthdaysToday = birthdays.birthdays.filter((b) => b.daysUntil === 0).length;
  // Im Spalten-Fluss ist die Karte nur halb so breit → höchstens zwei Kacheln nebeneinander.
  const birthdayGrid =
    effectiveLayout === 'spalten'
      ? 'grid grid-cols-1 gap-2.5 xl:grid-cols-2'
      : 'grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-4';

  const standDatum = new Date().toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const cell = CELL[effectiveLayout];

  if (!hasAccess) {
    return (
      <div className="mx-auto w-full max-w-7xl">
        <section className="rounded-2xl border border-info-200 bg-info-50 p-5 dark:border-info-800 dark:bg-info-900/20">
          <p className="text-sm leading-relaxed text-info-700 dark:text-info-300">
            Die vollständige Vereinsübersicht ist Vorstandsmitgliedern vorbehalten.
          </p>
        </section>
      </div>
    );
  }

  /* ---------------- abgeleitete Anzeigewerte ---------------- */

  const growthLabel = `${kpi.growthPercent >= 0 ? '+' : '−'}${dec1(Math.abs(kpi.growthPercent))} %`;
  const guestDiff = kpi.guestBookings - kpi.guestBookingsPrevious;
  const guestDiffLabel = `${guestDiff >= 0 ? '+' : '−'}${Math.abs(guestDiff)} vs. ${data.previousLabel}`;

  const cfMax = Math.max(1, ...data.cashflow.flatMap((m) => [m.ein, m.aus]));
  const gwMax = Math.max(1, ...data.guestTrend.map((w) => w.value));

  /** Termin laden und im gemeinsamen EventModal öffnen. */
  const openEvent = async (id: number) => {
    setEventLoadingId(id);
    try {
      const result = await fetchCalendarEvent(id);
      if (result) setEventModal(result);
    } catch (e) {
      console.error('Termin konnte nicht geladen werden', e);
    } finally {
      setEventLoadingId(null);
    }
  };

  const vaccinationLabel = (d: (typeof data.dogs)[number]) => {
    switch (d.vaccination) {
      case 'overdue':
        return `Überfällig ${ddmm(d.vaccinationUntil)}`;
      case 'soon':
        return `Läuft ab ${ddmm(d.vaccinationUntil)}`;
      case 'pending':
        return 'In Prüfung';
      case 'missing':
        return 'Kein Nachweis';
      default:
        return 'Aktuell';
    }
  };

  const liabilityLabel = (d: (typeof data.dogs)[number]) => {
    switch (d.liability) {
      case 'valid':
        return 'Vorhanden';
      case 'pending':
        return 'In Prüfung';
      default:
        return 'Fehlt';
    }
  };

  /** Handlungsbedarf — nur echte, aus den Daten abgeleitete Punkte. */
  const todos: {
    key: string;
    iconKey: AttributeIconKey;
    tone: 'error' | 'warning' | 'info' | 'primary';
    text: string;
    meta: string;
    href: string;
  }[] = [];

  if (dogStats.openVaccination > 0) {
    todos.push({
      key: 'impf',
      iconKey: 'vaccination',
      tone: 'error',
      text: `${dogStats.openVaccination} Hunde mit offener Impfung`,
      meta:
        dogStats.overdueVaccination > 0
          ? `${dogStats.overdueVaccination} überfällig — Trainingsteilnahme prüfen`
          : 'Nachweise anfordern',
      href: '/dog-management-beta',
    });
  }
  if (dogStats.missingLiability > 0) {
    todos.push({
      key: 'haftpflicht',
      iconKey: 'liability',
      tone: 'warning',
      text: `${dogStats.missingLiability} fehlende Haftpflicht-Nachweise`,
      meta: 'Erinnerung an Halter versenden',
      href: '/dog-management-beta',
    });
  }
  if (open.consumption > 0.005) {
    todos.push({
      key: 'verzehr',
      iconKey: 'drinksAccount',
      tone: 'info',
      text: `Offene Verzehrkosten ${eur(open.consumption)}`,
      meta: `${open.consumptionMembers} Mitglieder`,
      href: '/verzehrkosten',
    });
  }
  if (open.contributionMembers > 0) {
    todos.push({
      key: 'beitraege',
      iconKey: 'amount',
      tone: 'primary',
      text: `${open.contributionMembers} Mitglieder mit offenen Beiträgen`,
      meta: `${open.contributionYears} Beitragsjahre offen`,
      href: '/members',
    });
  }

  const TODO_TONE: Record<'error' | 'warning' | 'info' | 'primary', string> = {
    error: 'bg-error-50 text-error-600 dark:bg-error-900/20 dark:text-error-400',
    warning: 'bg-warning-50 text-warning-600 dark:bg-warning-900/20 dark:text-warning-400',
    info: 'bg-info-50 text-info-600 dark:bg-info-900/20 dark:text-info-400',
    primary: 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400',
  };

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 pb-8 xl:space-y-5">
      {/* ---------------- Seitenkopf (Muster von /my-bookings und /calendar) ---------------- */}
      <header className="app-page-header overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        <div className="flex flex-col gap-5 p-5 sm:p-7 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
              <MatrixIcon iconKey="dashboard" className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">
                Vorstandsbereich
              </p>
              <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-[28px]">
                {greeting()}
                {user?.vorname ? `, ${user.vorname}` : ''}
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Der Überblick für den Verein — Stand {standDatum}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* ---------------- Zeitraum + Kartenanordnung ---------------- */}
      {/* Die Kartenanordnung wirkt erst ab sm — mobil ist es immer eine Spalte,
          deshalb ist die Auswahl dort ausgeblendet. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
        <TabGroup tabs={PERIOD_TABS} active={period} onChange={setPeriod} ariaLabel="Zeitraum" />
        <div className="hidden sm:block">
          <TabGroup tabs={LAYOUT_TABS} active={layout} onChange={setLayout} ariaLabel="Kartenanordnung" />
        </div>
      </div>

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-warning-200 bg-warning-50 px-4 py-3 dark:border-warning-800 dark:bg-warning-900/20">
          <span className="text-sm text-warning-700 dark:text-warning-300">{error}</span>
          <button
            type="button"
            onClick={refresh}
            className="inline-flex items-center gap-1.5 rounded-lg border border-warning-300 px-2.5 py-1 text-xs font-semibold text-warning-800 transition-colors hover:bg-warning-100 dark:border-warning-700 dark:text-warning-200 dark:hover:bg-warning-900/40"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Neu laden
          </button>
        </div>
      )}

      {/* ---------------- KPI-Zeile (Issue #10) ---------------- */}
      <StatGroup columns={4} mobileColumns={2}>
        <StatCard
          icon={getAttributeIcon('participants')!}
          label="Aktive Mitglieder"
          value={loading ? '…' : kpi.activeMembers}
          sub={
            loading ? (
              ''
            ) : (
              <StatSub
                short={growthLabel}
                full={`${kpi.newMembers} Neu · ${kpi.leftMembers} Austritte · ${growthLabel}`}
              />
            )
          }
          tone="primary"
        />
        <StatCard
          icon={getAttributeIcon('guest')!}
          label="Gäste & Probetraining"
          value={loading ? '…' : kpi.guestBookings}
          sub={
            loading ? (
              ''
            ) : (
              <StatSub
                short={guestDiffLabel}
                // "Neuanmeldungen", nicht "Aufnahmen": ein Gast wird nicht in
                // den Verein aufgenommen, er legt sich ein Konto an. Die Zahl
                // kommt aus members.registriert_am (siehe Migration 009).
                full={`${kpi.newGuests} Neuanmeldungen · ${guestDiffLabel}`}
              />
            )
          }
          tone="info"
        />
        <StatCard
          icon={getAttributeIcon('booking')!}
          label="Ø Buchungen / Termin"
          value={loading ? '…' : dec1(kpi.avgBookings)}
          sub={
            loading ? (
              ''
            ) : (
              <StatSub
                short={`${kpi.utilization} % belegt`}
                full={`Auslastung ${kpi.utilization} % · ${kpi.events} Termine`}
              />
            )
          }
          tone="success"
        />
        <StatCard
          icon={getAttributeIcon('assets')!}
          label="Vereinsvermögen"
          value={loading ? '…' : eur0(kpi.totalBalance)}
          sub={
            loading ? (
              ''
            ) : kpi.reserves > 0 ? (
              <StatSub
                short={`Rücklage ${eur0(kpi.reserves)}`}
                full={`Rücklage ${eur0(kpi.reserves)} gebunden`}
              />
            ) : (
              `${kpi.accountCount} Konten`
            )
          }
          tone="warning"
          valueTone={kpi.totalBalance < 0 ? 'error' : 'neutral'}
        />
      </StatGroup>

      {/* ---------------- Karten-Raster ---------------- */}
      <div className={GRID[effectiveLayout]}>
        {/* ===== Termine & Auslastung ===== */}
        <div className={cell.termine}>
          <WidgetShell
            icon={getAttributeIcon('event')!}
            iconColor="primary"
            title="Termine & Auslastung"
            subtitle={`Buchungen je Termin ${PERIOD_PHRASE[period]}`}
            actions={<HeaderLink label="Kalender" onClick={() => navigate('/calendar')} />}
            className="h-full"
            footer={
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>
                  {loading
                    ? '…'
                    : `${data.events.length} von ${kpi.events} Terminen · ${kpi.bookings} Buchungen bei ${kpi.capacity} Plätzen`}
                </span>
                <span className="inline-flex items-center gap-1.5 font-semibold">
                  <MatrixIcon iconKey="utilization" className="h-3.5 w-3.5" />
                  {loading ? '…' : `Auslastung ${kpi.utilization} %`}
                </span>
              </div>
            }
          >
            {loading ? (
              <Skeleton rows={5} height="h-11" />
            ) : data.events.length === 0 ? (
              <EmptyHint>Keine Termine {PERIOD_PHRASE[period]}.</EmptyHint>
            ) : (
              <ul className="space-y-3.5">
                {data.events.map((ev) => {
                  const d = parseDay(ev.start);
                  const ratio = ev.capacity > 0 ? ev.count / ev.capacity : 0;
                  const bar =
                    ev.capacity <= 0
                      ? 'bg-gray-300 dark:bg-gray-600'
                      : ratio >= 0.9
                      ? 'bg-warning-500'
                      : ratio < 0.55
                      ? 'bg-gray-400 dark:bg-gray-500'
                      : 'bg-primary-600';
                  const time = [ev.startTime, ev.endTime].filter(Boolean).join(' – ') || 'Ganztägig';
                  return (
                    <li key={ev.id}>
                      <button
                        type="button"
                        onClick={() => openEvent(ev.id)}
                        disabled={eventLoadingId === ev.id}
                        aria-label={`Termin ${ev.title} öffnen`}
                        title={ev.isPast ? 'Bereits vergangener Termin' : 'Termindetails öffnen'}
                        className={`flex w-full items-center gap-3 rounded-lg text-left transition-colors hover:bg-gray-50 disabled:opacity-60 dark:hover:bg-gray-700/30 ${
                          ev.isPast ? 'opacity-60' : ''
                        }`}
                      >
                      <div className="w-10 flex-none rounded-lg bg-gray-100 py-1 text-center dark:bg-gray-700/50">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {d ? WD[d.getDay()] : '—'}
                        </div>
                        <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                          {d ? String(d.getDate()).padStart(2, '0') : '—'}
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                            {ev.title}
                          </span>
                          <span className="flex-none text-xs tabular-nums text-gray-600 dark:text-gray-300">
                            {ev.capacity > 0 ? `${ev.count} / ${ev.capacity}` : ev.count}
                          </span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-2.5">
                          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700/50">
                            <div
                              className={`h-full rounded-full ${bar}`}
                              style={{
                                width:
                                  ev.capacity > 0
                                    ? `${Math.min(100, Math.round(ratio * 100))}%`
                                    : ev.count > 0
                                    ? '100%'
                                    : '0%',
                              }}
                            />
                          </div>
                          <span className="w-24 flex-none text-right text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
                            {eventLoadingId === ev.id ? 'Lädt…' : time}
                          </span>
                        </div>
                      </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </WidgetShell>
        </div>

        {/* ===== Gästeanzahl ===== */}
        <div className={cell.gaeste}>
          <WidgetShell
            icon={getAttributeIcon('guest')!}
            iconColor="info"
            title="Gästeanzahl"
            subtitle={`Gästekarten-Buchungen ${PERIOD_BUCKETS[period]}`}
            actions={<HeaderLink label="Gästekarten" onClick={() => navigate('/guest-cards')} />}
            className="h-full"
            footer={
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {loading
                  ? '…'
                  : `${kpi.guestBookings} Buchungen ${PERIOD_PHRASE[period]} · ${kpi.newGuests} neu angemeldet · ${guestDiffLabel}`}
              </p>
            }
          >
            <div className="mb-1 flex items-end gap-1 sm:gap-1.5">
              {(data.guestTrend.length > 0
                ? data.guestTrend
                : Array.from({ length: 4 }, () => ({ label: '—', value: 0 }))
              ).map((w, i) => (
                <BarColumn
                  key={`${w.label}-${i}`}
                  label={w.label}
                  value={loading ? '' : w.value}
                  title={`${w.label}: ${w.value} Buchungen`}
                >
                  <div
                    className={`w-full rounded-t ${
                      loading
                        ? 'bg-gray-100 dark:bg-gray-700/50'
                        : w.value <= 0
                        ? 'bg-gray-200 dark:bg-gray-700'
                        : w.value === gwMax
                        ? 'bg-primary-600'
                        : 'bg-primary-200 dark:bg-primary-900/50'
                    }`}
                    style={{ height: loading ? '20%' : barHeight(w.value, gwMax) }}
                  />
                </BarColumn>
              ))}
            </div>

            <div className="my-3.5 border-t border-gray-100 dark:border-gray-700/70" />

            <Eyebrow className="mb-2.5">Angemeldete Gäste</Eyebrow>
            {loading ? (
              <Skeleton rows={3} height="h-8" />
            ) : data.guests.length === 0 ? (
              <EmptyHint>Keine Gastbuchungen {PERIOD_PHRASE[period]}.</EmptyHint>
            ) : (
              <ul className="space-y-2.5">
                {data.guests.map((g) => (
                  <li key={g.id}>
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedGuest({
                          mg_nr: String(g.memberId),
                          vorname: g.vorname,
                          name: g.name,
                          mitglied_nr: g.memberNumber ? String(g.memberNumber) : undefined,
                        })
                      }
                      aria-label={`Details zu ${g.vorname} ${g.name} öffnen`}
                      title={g.isPast ? 'Bereits vergangener Termin' : 'Gastdetails öffnen'}
                      className={`flex w-full items-center gap-2.5 rounded-lg text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                        g.isPast ? 'opacity-60' : ''
                      }`}
                    >
                    <MemberAvatar member={{ vorname: g.vorname, name: g.name }} role="guest" size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                        {`${g.vorname} ${g.name}`.trim()}
                      </div>
                      <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">{g.info}</div>
                    </div>
                    <span className="flex-none text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
                      {ddmm(g.start)}
                    </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </WidgetShell>
        </div>

        {/* ===== Finanzübersicht ===== */}
        <div className={cell.finanzen}>
          <WidgetShell
            icon={getAttributeIcon('balance')!}
            iconColor="success"
            title="Finanzübersicht"
            subtitle={`Konten (aktuell) und Cashflow ${PERIOD_PHRASE[period]}`}
            actions={<HeaderLink label="Auszüge" onClick={() => navigate('/account-statements')} />}
            className="h-full"
            footer={
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <Eyebrow>Offene Verzehrkosten</Eyebrow>
                  <p className="text-sm font-bold tabular-nums text-error-600 dark:text-error-400">
                    {loading ? '…' : `${eur(open.consumption)} · ${open.consumptionMembers} Mitglieder`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/verzehrkosten')}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700/50"
                >
                  Erinnerung senden
                  <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            }
          >
            {loading ? (
              <Skeleton rows={4} height="h-6" />
            ) : data.accounts.length === 0 ? (
              <EmptyHint>Keine aktiven Konten hinterlegt.</EmptyHint>
            ) : (
              <ul className="space-y-2.5">
                {data.accounts.map((acc) => (
                  <li key={acc.id} className="flex items-center gap-2.5">
                    <span className="flex-none text-gray-500 dark:text-gray-400">
                      <MatrixIcon iconKey={ACCOUNT_ICON[acc.iconKey]} />
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-700 dark:text-gray-200">
                      {acc.name}
                    </span>
                    <span
                      className={`flex-none text-sm font-semibold tabular-nums ${
                        acc.balance < 0
                          ? 'text-error-600 dark:text-error-400'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {eur(acc.balance)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="my-4 border-t border-gray-100 dark:border-gray-700/70" />

            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <Eyebrow>Einnahmen / Ausgaben {PERIOD_BUCKETS[period]}</Eyebrow>
              <span className="flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm bg-primary-500" />
                  {loading ? 'Ein' : eur0(kpi.cashIn)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm bg-gray-300 dark:bg-gray-600" />
                  {loading ? 'Aus' : eur0(kpi.cashOut)}
                </span>
              </span>
            </div>

            <div className="flex items-end gap-1 sm:gap-2">
              {data.cashflow.map((m, i) => (
                <BarColumn
                  key={`${m.label}-${i}`}
                  label={m.label}
                  trackClass="h-24"
                  title={`${m.label} — Einnahmen ${eur(m.ein)}, Ausgaben ${eur(m.aus)}`}
                >
                  <div
                    className={`w-1.5 rounded-t sm:w-2 ${
                      m.ein > 0 ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                    style={{ height: barHeight(m.ein, cfMax) }}
                  />
                  <div
                    className={`w-1.5 rounded-t sm:w-2 ${
                      m.aus > 0 ? 'bg-gray-300 dark:bg-gray-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                    style={{ height: barHeight(m.aus, cfMax) }}
                  />
                </BarColumn>
              ))}
            </div>
          </WidgetShell>
        </div>

        {/* ===== Hundeübersicht ===== */}
        <div className={cell.hunde}>
          <WidgetShell
            icon={getAttributeIcon('dog')!}
            iconColor="warning"
            title="Hundeübersicht"
            subtitle="Impfstatus und Pflichtnachweise der gemeldeten Hunde"
            actions={<HeaderLink label="Alle Hunde" onClick={() => navigate('/dog-management-beta')} />}
            className="h-full"
            footer={
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {loading
                  ? '…'
                  : `${dogStats.total} gemeldete Hunde · ${dogStats.openVaccination} offene Impfungen · ${dogStats.missingLiability} fehlende Haftpflicht`}
              </p>
            }
          >
            {/* Kopfzeile nur ab sm — mobil trägt jede Zeile ihre eigenen Badges */}
            <div className="hidden border-b border-gray-200 pb-2 dark:border-gray-700 sm:grid sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_150px_150px] sm:gap-3">
              <Eyebrow>Hund</Eyebrow>
              <Eyebrow>Halter</Eyebrow>
              <Eyebrow>Tollwutimpfung</Eyebrow>
              <Eyebrow>Haftpflicht</Eyebrow>
            </div>

            {loading ? (
              <div className="pt-3">
                <Skeleton rows={6} height="h-8" />
              </div>
            ) : data.dogs.length === 0 ? (
              <div className="pt-3">
                <EmptyHint>Keine Hunde gemeldet.</EmptyHint>
              </div>
            ) : (
              <ul>
                {data.dogs.map((d) => (
                  <li key={d.id} className="border-b border-gray-100 dark:border-gray-700/70">
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedDog({
                          id: d.id,
                          name: d.name,
                          owner: d.owner,
                          ownerIsGuest: d.ownerIsGuest,
                        })
                      }
                      aria-label={`Details zu ${d.name} öffnen`}
                      className="grid w-full grid-cols-1 gap-1.5 rounded-lg py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30 sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_150px_150px] sm:items-center sm:gap-3"
                    >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">{d.name}</div>
                      <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                        {[d.breed || 'Rasse unbekannt', d.ageYears != null ? `${d.ageYears} J.` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                    <div className="flex min-w-0 items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                      <span className="sm:hidden">Halter: </span>
                      <span className="truncate">{d.owner || '—'}</span>
                      {d.ownerIsGuest && <GuestBadge />}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <DocBadge status={d.vaccination}>{vaccinationLabel(d)}</DocBadge>
                      <span className="sm:hidden">
                        <DocBadge status={d.liability}>Haftpflicht: {liabilityLabel(d)}</DocBadge>
                      </span>
                    </div>
                    <div className="hidden sm:block">
                      <DocBadge status={d.liability}>{liabilityLabel(d)}</DocBadge>
                    </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </WidgetShell>
        </div>

        {/* ===== Mitgliederübersicht ===== */}
        <div className={cell.mitglieder}>
          <WidgetShell
            icon={getAttributeIcon('member')!}
            iconColor="primary"
            title="Mitgliederübersicht"
            subtitle="Rollenverteilung (aktuell) und neue Aufnahmen"
            actions={<HeaderLink label="Mitglieder" onClick={() => navigate('/members')} />}
            className="h-full"
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
              <div className="space-y-3">
                {loading ? (
                  <Skeleton rows={5} height="h-6" />
                ) : (
                  data.roleRows.map((r) => (
                    <div key={r.role} className="flex items-center gap-2.5">
                      <span className="inline-flex w-28 flex-none items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                        <MatrixIcon iconKey={ROLE_ICON[r.role]} className="h-3.5 w-3.5" />
                        <span className="truncate">{ROLE_LABEL[r.role]}</span>
                      </span>
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700/50">
                        <div
                          className={`h-full rounded-full ${ROLE_BAR[r.role]}`}
                          style={{ width: `${r.pct}%` }}
                        />
                      </div>
                      <span className="w-8 flex-none text-right text-xs tabular-nums text-gray-600 dark:text-gray-300">
                        {r.count}
                      </span>
                    </div>
                  ))
                )}
              </div>

              <div>
                <Eyebrow className="mb-2.5">Neu aufgenommen {PERIOD_PHRASE[period]}</Eyebrow>
                {loading ? (
                  <Skeleton rows={4} height="h-8" />
                ) : data.newMembers.length === 0 ? (
                  <EmptyHint>Keine Neuaufnahmen {PERIOD_PHRASE[period]}.</EmptyHint>
                ) : (
                  <ul className="space-y-2.5">
                    {data.newMembers.map((m) => (
                      <li key={m.id} className="flex items-center gap-2.5">
                        <MemberAvatar
                          member={{ vorname: m.vorname, name: m.name }}
                          role={m.roleName}
                          size="sm"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                            {`${m.vorname} ${m.name}`.trim()}
                          </div>
                          <div className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                            <MatrixIcon iconKey={ROLE_ICON[m.role]} className="h-3 w-3" />
                            {ROLE_LABEL[m.role]}
                            {m.memberNumber ? ` · Nr. ${m.memberNumber}` : ''}
                          </div>
                        </div>
                        <span className="flex-none text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
                          {ddmm(m.since)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </WidgetShell>
        </div>

        {/* ===== Handlungsbedarf ===== */}
        <div className={cell.todos}>
          <WidgetShell
            icon={getAttributeIcon('warning')!}
            iconColor="error"
            title="Handlungsbedarf"
            subtitle="Was der Vorstand diese Woche entscheiden sollte"
            titleRight={
              !loading && todos.length > 0 ? (
                <span className="rounded-full bg-warning-100 px-2 py-0.5 text-[11px] font-bold text-warning-700 dark:bg-warning-900/30 dark:text-warning-300">
                  {todos.length} offen
                </span>
              ) : undefined
            }
            className="h-full"
          >
            {loading ? (
              <Skeleton rows={4} height="h-8" />
            ) : todos.length === 0 ? (
              <div className="flex items-start gap-2.5">
                <span className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-success-50 text-success-600 dark:bg-success-900/20 dark:text-success-400">
                  <MatrixIcon iconKey="active" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">Keine offenen Punkte</p>
                  <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                    Der aktuelle Vereinsbetrieb sieht gut aus.
                  </p>
                </div>
              </div>
            ) : (
              <ul className="space-y-3">
                {todos.map((t) => (
                  <li key={t.key}>
                    <button
                      type="button"
                      onClick={() => navigate(t.href)}
                      className="group flex w-full items-start gap-2.5 rounded-lg text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30"
                    >
                      <span
                        className={`flex h-7 w-7 flex-none items-center justify-center rounded-lg ${TODO_TONE[t.tone]}`}
                      >
                        <MatrixIcon iconKey={t.iconKey} className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-gray-900 dark:text-white">
                          {t.text}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-gray-500 dark:text-gray-400">
                          {t.meta}
                        </span>
                      </span>
                      <ArrowRightIcon
                        className="mt-1 h-4 w-4 flex-none text-gray-300 transition-transform group-hover:translate-x-0.5 dark:text-gray-600"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </WidgetShell>
        </div>

        {/* ===== Geburtstage ===== */}
        <div className={cell.geburtstage}>
          <WidgetShell
            icon={getAttributeIcon('birthday')!}
            iconColor="primary"
            title="Geburtstage"
            subtitle="Die nächsten Geburtstage im Verein"
            titleRight={
              !birthdays.loading && birthdaysToday > 0 ? (
                <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-bold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                  {birthdaysToday === 1 ? 'Heute' : `${birthdaysToday} heute`}
                </span>
              ) : undefined
            }
            actions={<HeaderLink label="Mitglieder" onClick={() => navigate('/members')} />}
            className="h-full"
          >
            {birthdays.loading ? (
              <div className={birthdayGrid}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-700/50" />
                ))}
              </div>
            ) : birthdays.error ? (
              <EmptyHint>{birthdays.error}</EmptyHint>
            ) : birthdays.birthdays.length === 0 ? (
              <EmptyHint>Keine Geburtstage in den nächsten Wochen.</EmptyHint>
            ) : (
              <ul className={birthdayGrid}>
                {birthdays.birthdays.map((b) => (
                  <BirthdayTile key={b.id} b={b} baseUrl={BASE_URL} />
                ))}
              </ul>
            )}
          </WidgetShell>
        </div>
      </div>

      {/* Termindetails — dasselbe EventModal wie im Kalender */}
      {eventModal && (
        <EventModal
          event={eventModal.event}
          events={eventModal.sameDay}
          onClose={() => setEventModal(null)}
          onDelete={(response: any) => {
            if (response?.success) {
              setEventModal(null);
              refresh();
            }
          }}
          onBook={() => refresh()}
          refreshCalendar={refresh}
          onEventSelect={(event: any) =>
            setEventModal((prev) => (prev ? { ...prev, event } : prev))
          }
          onEdit={(event: any) => {
            setEditingEvent(event);
            setEventModal(null);
          }}
        />
      )}

      {editingEvent && (
        <EditEventModal
          event={editingEvent}
          isOpen={editingEvent !== null}
          onClose={() => setEditingEvent(null)}
          refreshCalendar={refresh}
        />
      )}

      {/* Gastdetails — dieselbe Komponente wie /guest-cards */}
      <GuestDetailModal
        open={selectedGuest !== null}
        onClose={() => setSelectedGuest(null)}
        guest={selectedGuest}
        onChanged={refresh}
      />

      {/* Hundedetails — dieselbe Karte und dieselben Aktionen wie /dog-management-beta */}
      <DogDetailModal
        open={selectedDog !== null}
        onClose={() => setSelectedDog(null)}
        dogId={selectedDog?.id ?? null}
        fallbackName={selectedDog?.name}
        owner={selectedDog?.owner}
        ownerIsGuest={selectedDog?.ownerIsGuest}
        onChanged={refresh}
        onOpenManagement={() => {
          setSelectedDog(null);
          navigate('/dog-management-beta');
        }}
      />
    </div>
  );
};

export default Dashboard;
